import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AgrirouterService } from './agrirouter.service';
import {
  GeoServerLayerId,
  MAIN_NAMESPACES,
  WFSFilterParams,
  WPSComplexDataInput,
  WPSExecute,
  WPSLateralInput,
  WPSLayerInput,
  WPSSubprocessInput,
} from './wps.service';
import { AuthenticationService } from './authentication.service';

type PointsStatistics = {
  min: number;
  max: number;
};

const constructWPSRequest = (
  pointLayerId: GeoServerLayerId,
  targetAttribute: string,
  boundaryLayerId: GeoServerLayerId,
  boundaryFeatureFilter?: WFSFilterParams
) => {
  const mainWpsExecute = new WPSExecute('gs:Aggregate', 'application/json', [
    new WPSSubprocessInput('features', 'gs:Clip', 'text/xml; subtype=wfs-collection/1.0', [
      new WPSLayerInput('features', pointLayerId),
      new WPSSubprocessInput(
        'clip',
        'gs:CollectGeometries',
        'text/xml; subtype=wfs-collection/1.0',
        [new WPSLayerInput('features', boundaryLayerId, boundaryFeatureFilter)]
      ),
    ]),
    new WPSLateralInput('aggregationAttribute', targetAttribute),
    new WPSLateralInput('function', 'Min'),
    new WPSLateralInput('function', 'Max'),
    new WPSLateralInput('singlePass', 'false'),
  ]);

  return `<?xml version="1.0" encoding="UTF-8"?>
    ${mainWpsExecute.toWPS(MAIN_NAMESPACES, true)}
  `;
};

const constructWPSAttributesRangeRequest = (
  layerId: GeoServerLayerId,
  boundaryFeatureFilter: WFSFilterParams
) => {
  const mainWpsExecute = new WPSExecute('gs:AttributesRange', 'application/json', [
    new WPSLayerInput('features', layerId, boundaryFeatureFilter),
  ]);

  return `<?xml version="1.0" encoding="UTF-8"?>
    ${mainWpsExecute.toWPS(MAIN_NAMESPACES, true)}
  `;
};

const constructBoundsWPSRequest = (layerId: GeoServerLayerId, featureFilter: WFSFilterParams) => {
  const mainWpsExecute = new WPSExecute('JTS:envelope', 'application/json', [
    new WPSSubprocessInput('geom', 'gs:CollectGeometries', 'text/xml; subtype=wfs-collection/1.0', [
      new WPSLayerInput('features', layerId, featureFilter),
    ]),
  ]);

  return `<?xml version="1.0" encoding="UTF-8"?>
    ${mainWpsExecute.toWPS(MAIN_NAMESPACES, true)}
  `;
};

@Injectable()
export class GeoserverService {
  constructor(
    private agrirouterService: AgrirouterService,
    private authenticationService: AuthenticationService
  ) {}

  getGeoServerLayerId(filename: string, layer: string): GeoServerLayerId {
    const namespace = 'u' + this.agrirouterService.getExternalId();
    const layerName = layer + '_' + filename.replace(/-/g, '').replace(/\.\w+$/, '');
    return {
      namespace,
      namespaceURI: `https://www.isoxml-service.de/${namespace}`,
      id: layerName,
    };
  }

  // These transformation must be in sync with the server's transformations
  getMVTURL(filename: string, layer: string): string {
    const layerInfo = this.getGeoServerLayerId(filename, layer);
    return `${environment.geoserverUrl}gwc/service/tms/1.0.0/${layerInfo.namespace}:${layerInfo.id}@EPSG%3A900913@pbf/{z}/{x}/{-y}.pbf`;
  }

  getDeckGLLoadOptions() {
    const auth_tokens = this.authenticationService.getAuthToken();
    if (!auth_tokens || !auth_tokens.access_token) {
      console.error("Errore: Token non disponibile per l'autenticazione a GeoServer.");
      return { fetch: {} }; // Nessuna autenticazione, errore garantito (401)
    }

    return {
      fetch: {
        headers: {
          Authorization: `Bearer ${auth_tokens.access_token}`,
          Accept: '*/*',
        },
      },
    };
  }

  createLayers(filename: string) {
    const auth_headers = this.getDeckGLLoadOptions().fetch.headers;
    return from(
      fetch(
        `${environment.geoserverUrl}isoxml-create?filename=${encodeURIComponent(filename)}&agrirouter-uuid=${encodeURIComponent(this.agrirouterService.getExternalId())}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...auth_headers, // Assicura il token
          },
        }
      )
    );
  }

  getFeatureAttributesRange(layerId: GeoServerLayerId, featureFilter: WFSFilterParams) {
    const wpsRequest = constructWPSAttributesRangeRequest(layerId, featureFilter);
    const fetchOptions = this.getDeckGLLoadOptions().fetch;
    return from(
      fetch(`${environment.geoserverUrl}ows?service=wps&version=1.0.0&request=Execute`, {
        method: 'POST',
        body: wpsRequest,
        ...fetchOptions,
        headers: {
          'Content-Type': 'text/xml',
          ...fetchOptions.headers,
        },
      })
        .then((response) => response.json())
        .catch(() => null)
    );
  }

  getFeatureBounds(layerId: GeoServerLayerId, featureFilter: WFSFilterParams) {
    const wpsRequest = constructBoundsWPSRequest(layerId, featureFilter);
    const fetchOptions = this.getDeckGLLoadOptions().fetch;
    return from(
      fetch(`${environment.geoserverUrl}ows?service=wps&version=1.0.0&request=Execute`, {
        method: 'POST',
        body: wpsRequest,
        ...fetchOptions,
        headers: {
          'Content-Type': 'text/xml',
          ...fetchOptions.headers,
        },
      })
        .then((response) => response.json())
        .catch(() => null)
    );
  }

  getStatistics(
    pointLayerId: GeoServerLayerId,
    targetAttribute: string,
    boundaryLayerId: GeoServerLayerId,
    boundaryFeatureFilter?: WFSFilterParams
  ): Observable<PointsStatistics> {
    const wpsRequest = constructWPSRequest(
      pointLayerId,
      targetAttribute,
      boundaryLayerId,
      boundaryFeatureFilter
    );
    const fetchOptions = this.getDeckGLLoadOptions().fetch;
    return from(
      fetch(`${environment.geoserverUrl}ows?service=wps&version=1.0.0&request=Execute`, {
        method: 'POST',
        body: wpsRequest,
        ...fetchOptions,
        headers: {
          'Content-Type': 'text/xml',
          ...fetchOptions.headers,
        },
      })
        .then((response) => response.json())
        .then((stats) => ({
          min: stats.AggregationResults[0][0],
          max: stats.AggregationResults[0][1],
        }))
        .catch(() => null)
    );
  }

  uploadFile(file: File): Observable<{ uuid: string }> {
    const fetchOptions = this.getDeckGLLoadOptions().fetch;
    const formData = new FormData();
    formData.append('file', file, file.name);
    return from(
      fetch(`${environment.geoserverUrl}isoxml-upload`, {
        method: 'POST',
        body: formData,
        ...fetchOptions,
        headers: {
          ...fetchOptions.headers,
        },
      })
        .then((response) => response.json())
        .catch(() => null)
    );
  }

  downloadTimelog(layerId: GeoServerLayerId, featureFilter: WFSFilterParams) {
    const mainWpsExecute = new WPSExecute('gs:Query', 'application/zip', [
      new WPSComplexDataInput(
        'filter',
        `${featureFilter.attributeName} = '${featureFilter.value}'`,
        'text/plain; subtype=cql'
      ),
      new WPSLayerInput('features', layerId),
    ]);

    const wpsRequest = `<?xml version="1.0" encoding="UTF-8"?>
      ${mainWpsExecute.toWPS(MAIN_NAMESPACES, true)}
    `;

    const fetchOptions = this.getDeckGLLoadOptions().fetch;

    return from(
      fetch(`${environment.geoserverUrl}ows?service=wps&version=1.0.0&request=Execute`, {
        method: 'POST',
        body: wpsRequest,
        ...fetchOptions,
        headers: {
          'Content-Type': 'text/xml',
          ...fetchOptions.headers,
        },
      })
        .then((response) => response.blob())
        .catch(() => null)
    );
  }
}
